import React, { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import useLzFApiCall from "@lz/services/Api";
import { useLzOnMount } from "@lz/services/Hooks";
import { LzTFormSizes, LzTOpenObject } from "@lz/typings/types";
import { lzFShowSuccessMessage, useLzFUrlQueryParams } from "@lz/services/Utils";
import LzMCrud from "@lz/models/crud/Crud";
import LzMBase, { LzTBase } from "@lz/models/commons/Base";
import LzXForm from "./Form";

export interface LzPCrudAdd {
    crud: LzMCrud,
    modelRef: LzTBase,
    initModelData?: LzTOpenObject;
    formSize?: LzTFormSizes,
    children?: React.ReactNode;
}

const LzXCrudAdd = (props: LzPCrudAdd) => {
    if (typeof props.crud === 'undefined' || !props.crud || '' === props.crud.ident) {
        return <Alert variant="filled" severity="error">CRUD configurations is missing!</Alert>;
    }

    if (typeof props.modelRef === 'undefined' || !props.modelRef) {
        return <Alert variant="filled" severity="error">Model definition is missing!</Alert>;
    }

    const [model, setModel] = useState<LzMBase | null>(null);

    const routeParams = useParams();
    const lzApiCall = useLzFApiCall();
    const navigate = useNavigate();
    const location = useLocation();
    const lzFUrlQueryParams = useLzFUrlQueryParams();
    const locState: LzTOpenObject = location.state as LzTOpenObject;

    // console.log('LOCATION', location);
    // console.log('LOCSTATE', locState);

    const loadRecord = async (id: number) => {
        const data = await lzApiCall(props.crud.ident + '/' + id);

        if (data) {
            return LzMBase.buildModel(data, props.modelRef);
        }

        return null;
    };

    const buildModelFromDataAndCRUD = (data: LzMBase | null): LzMBase => {
        // Initialize the new model with location state data (to load any matching attributes)
        const newModel = new props.modelRef(Object.assign({}, locState, props.initModelData || {}));
        // console.log('MODEL CREATED', newModel, props);

        const keys = Object.keys(props.crud.addFormInputs);
        for (const key of keys) {
            // If the data is already provided in the initModelData for this key, then skip it to keep that data always
            if (typeof props.initModelData !== 'undefined' && typeof props.initModelData[key] !== 'undefined') {
                continue;
            }

            // Check parent field value
            if ('parent_id' === key && data?.id) {
                newModel.parent_id = data.id;
                newModel.parent_name = props.crud.parentName(data);
            }
            // For all other fields
            else {
                // Check if the field value should be preserved
                if (props.crud.addFormInputs[key].preserveValueAfterSubmit) {
                    // Only set to the model value if it exists there, else if will keep the value initialized by the `locState`
                    if (typeof data?.[key] !== 'undefined') {
                        newModel[key] = data[key];
                    }
                } else {
                    newModel[key] = props.crud.addFormInputs[key].default;
                }
            }
        }

        return newModel;
    };

    const handleOnMount = async () => {
        const parentId = +(routeParams.id || 0);
        const parent = parentId > 0 ? await loadRecord(parentId) : null;
        setModel(buildModelFromDataAndCRUD(parent));
    };

    useLzOnMount(() => {
        handleOnMount();
    });


    if (!model) {
        return <CircularProgress />
    }

    const handleOnSubmit = async (record: LzMBase): Promise<boolean> => {
        const data = await lzApiCall(props.crud.ident, record, 'post');
        if (data) {
            lzFShowSuccessMessage('Record added successfully!');

            const recData = Object.assign({}, record, { id: data });
            const routeAfterAdd = props.crud.getRouteAfterAdd(recData);
            if (routeAfterAdd !== '') {
                navigate(
                    routeAfterAdd,
                    typeof props.crud.getRouteAfterAddParams === 'undefined' ?
                        undefined :
                        { state: props.crud.getRouteAfterAddParams(recData) }
                );
            }

            return true;
        }
        return false;
    };

    const handleOnCancel = () => navigate(props.crud.getRouteOnAddClose(model, locState, lzFUrlQueryParams()), { state: locState });

    const isAnyPreservableField = Object.values(props.crud.addFormInputs).filter(v => v.preserveValueAfterSubmit).length > 0;

    return (
        <React.Fragment>

            <LzXForm
                fields={props.crud.fields}
                inputs={props.crud.addFormInputs}
                modelRef={props.modelRef}
                model={model}
                formSize={props.formSize}
                isTreeBased={props.crud.isTreeBased}
                onSubmit={handleOnSubmit}
                onReset={isAnyPreservableField ? buildModelFromDataAndCRUD : undefined}
                resetOnSubmit={props.crud.resetAfterAdd}
                onCancel={handleOnCancel}
                className={props.crud.className}
            />

        </React.Fragment>
    );
};

export default LzXCrudAdd;
